import { DEFAULT_GROUP_COVER_IMAGE, DEFAULT_GROUP_LOGO } from '@/helper/const'
import GroupMember, {
  GroupMemberRole,
  GroupMemberStatus,
  IGroupMember,
} from '@/models/GroupMember'
import Groups, { GroupType, IGroup, MemberStatus } from '@/models/Groups'
import { GroupsService, ListingsService, UsersService } from '@/service'
import { getSessionCookieData, isGokollab } from '@ghl/clientportal-core/init'
import { computed, reactive, ref } from 'vue'

import { UserState } from '@/composition/users'
import config from '@/config'
import { EventName } from '@/models/TrackEvent'
import { IPaginationParams } from '@/service/types'
import { useAppStore } from '@/store/app'
import { AnalyticsState } from './analytics'
import { FormState } from './forms'
import { LeaderboardState } from './leaderboard'

export enum SideBarList {
  GROUPS = 'GROUPS',
  CHANNELS = 'CHANNELS',
}
export enum GroupAccessType {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}
export interface Group {
  id: string
  slug: string
  name: string
  logo: any
  coverImage: string
  membersCount: number
  type: GroupType
}

export class GroupsState {
  state: any = reactive({
    userGroup: {} as any,
    processing: false,
    newlyJoined: false,
    fetching: false,
    fetchingPinnedGroups: false,
    allGroupsFetched: false,
    groupList: [] as any,
    pinnedGroupList: [] as Group[],
    unpinnedGroupList: [] as Group[],
    pinnedGroupIds: [] as string[],
    uniqueGroupsMap: new Map<number | string, any>(),
    groupData: {} as any,
    updatedGroupData: {} as any,
    membersLevelDistribution: new Map<string, number>(),
    isGroupDataUpdated: Boolean,
    groupOwner: {} as any,
    groupType: computed(() => {
      return GroupState.state?.groupData?.settings?.groupType ===
        GroupType.PRIVATE
        ? GroupType.PRIVATE
        : GroupType.PUBLIC
    }),
    channelList: [] as any,
    restrictedChannelList: [] as any, // channels where user cannot post
    nonRestrictedChannelList: [] as any, // channels where user can post
    showChannelList: false,
    role: null as any,
    orderId: null as any,
    userStatus: null as any,
    userMembershipAnswers: null as any,
    editorCurrentChannel: null as any, // this is used to store the current channel where post or comment editors are opened
    isGroupAdmin: computed(() => {
      return (
        (GroupState.state.role === 'ADMIN' ||
          GroupState.state.role === 'OWNER') &&
        GroupState.state.userStatus === MemberStatus.ACTIVE
      )
    }),
    groupAdmins: new Map<string, IGroupMember>(), // Map<contactId, GroupMember>
    groupMembers: new Array<IGroupMember>(),
    membersPageCurrentFilter: 'All',
    groupLink: computed(() => {
      if (isGokollab()) {
        return `https://${config.GOKOLLAB_DOMAIN}/${this.state.groupData.slug}/home`
      }
      return `https://${window.location.hostname}/communities/groups/${this.state.groupData.slug}/home`
    }),
    requestCount: 0,
    groupMembersAvatarOptions: [],
    closedGroup: ref(false),
    sideBarListView: ref(SideBarList.CHANNELS),
    groupInfoTopPosition: ref(0),
    isGroupColorsUpdated: false,
    routesWithNoBottomNavbar: [
      `group-member-list`,
      `group-invitations`,
      `to-review`,
      `communities-settings`,
      `conversations`,
      'chat-view',
      `user-profile`,
    ],
    routesWithBackButton: [`leaderboard`, `events`, 'profile'],
  })
  async getGroupBySlug(slug: string): Promise<any> {
    try {
      const group = await GroupsService.getGroupBySlug(slug)

      this.state.channelList = undefined
      if (!this.state.uniqueGroupsMap.has(slug)) {
        this.state.uniqueGroupsMap.set(slug, group.groupDetail)
      }
      let isPresentInGroupList = false
      this.state.groupList.forEach(group => {
        if (group.slug === slug) {
          isPresentInGroupList = true
        }
      })
      if (
        group.groupDetail?.settings?.groupSwitcherVisibility == false &&
        !isPresentInGroupList
      ) {
        this.state.groupList.push(group.groupDetail)
      }
      this.setGroupDataInState(group.groupDetail, false)
      this.state.groupOwner = group.owner
      if (!this.state.groupData.branding) {
        this.state.groupData.branding = {
          mode: 'light',
        }
      } else if (!this.state.groupData.branding?.mode) {
        this.state.groupData.branding.mode = 'light'
      }
      this.state.role = (group.user && group.user[0]?.role) || null
      this.state.userStatus = group.user
        ? group.user[0]?.status || MemberStatus.ACTIVE
        : undefined
      this.state.userMembershipAnswers =
        group.user && group.user[0]?.membershipAnswers
      this.state.userGroup = group.user ? group?.user[0] : undefined
      FormState.state.membershipQuestions = group.membershipQuestions

      return group
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      if (GroupState.state.role) {
        AnalyticsState.trackCommunitiesAccessEvent({
          groupId: GroupState.state.groupData?._id,
          status: GroupState.state.userStatus,
          locationId: GroupState.state.groupData?.locationId,
          eventName: EventName.COMMUNITIES_GROUP_ACCESS,
          eventData: {},
          role: GroupState.state.role,
        })
      }
    }
  }

  async setGroupDataInState(
    groupDetail: any,
    isUpdated = false
  ): Promise<void> {
    this.state.isGroupColorsUpdated = false
    this.state.groupData = groupDetail
    this.state.updatedGroupData = JSON.parse(JSON.stringify(groupDetail))

    if (!this.state.updatedGroupData?.settings) {
      this.state.updatedGroupData.settings = {}
    }
    if (!isUpdated) {
      LeaderboardState.clearState()
      UserState.reset()
    }
  }
  async fetchUser(): Promise<void> {
    const cookie = getSessionCookieData()
    useAppStore().setUserId(cookie?.contactId)
    await UserState.getCurrentUser()
    useAppStore().setAuthenticated(true)
  }
  async getGroupByLocation(
    query: IPaginationParams,
    skipCache?: boolean
  ): Promise<void> {
    if ((this.state.allGroupsFetched || this.state.fetching) && !skipCache) {
      return
    }
    try {
      this.state.fetching = true
      if (
        !query.skip &&
        query.skip !== 0 &&
        this.state.groupList.length &&
        !skipCache
      ) {
        query.skip = this.state.groupList.length - 1 // -1 to make sure we get the last group, as we are manually adding the current group to the list
      }
      const newGroupList = await GroupsService.getGroupByLocation(query)

      newGroupList.forEach(group => {
        const keyValue = group['slug']
        if (!this.state.uniqueGroupsMap.has(keyValue)) {
          this.state.uniqueGroupsMap.set(keyValue, group)
        }
      })
      this.state.groupList = Array.from(this.state.uniqueGroupsMap.values())

      if (newGroupList.length < query?.limit) {
        this.state.allGroupsFetched = true
      }
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      this.state.fetching = false
    }
  }
  async joinGroup(role = 'MEMBER'): Promise<void> {
    const appStore = useAppStore()
    if (appStore.userId) {
      const status =
        GroupState.state.groupType === GroupType.PRIVATE
          ? MemberStatus.REQUESTED
          : MemberStatus.ACTIVE

      if (UsersService === undefined) {
        await new Promise(resolve => {
          const interval = setInterval(() => {
            if (UsersService) {
              clearInterval(interval)
              resolve(true)
            }
          }, 100)
        })
      }
      const userGroup = await UsersService.joinGroup(
        role,
        appStore.userId,
        status,
        GroupState.state.orderId
      )
      if (UserState.state.currentUser) {
        if (!UserState.state.currentUser.contactId) {
          UserState.state.currentUser['contactId'] = userGroup.contactId
        }
      } else if (UserState?.state) {
        UserState.state['currentUser'] = {
          contactId: userGroup.contactId,
        } as any
      }
      GroupState.state.userStatus = userGroup.status
      GroupState.state.role = userGroup.role

      if (GroupState.state.userStatus === MemberStatus.ACTIVE) {
        GroupState.state.groupData.membersCount += 1
        GroupState.state.newlyJoined = true
      }
      setTimeout(() => {
        GroupState.state.newlyJoined = false
      }, 3000)
    } else {
      return
    }
  }
  async getGroupAdmins(): Promise<void> {
    const groupAdmins = await UsersService.getGroupMembersList([
      GroupMemberRole.ADMIN,
      GroupMemberRole.OWNER,
    ])

    this.state.groupAdmins = new Map<string, GroupMember>()
    groupAdmins.forEach((groupAdmin: GroupMember) => {
      this.state.groupAdmins.set(groupAdmin.contactId, groupAdmin)
    })
  }
  async getGroupMembersDetails(
    memberRole?: GroupMemberRole,
    memberStatus?: GroupMemberStatus,
    searchText?: string,
    paginationObject?: { limit: number; pageNo: number },
    showEmail?: boolean,
    isMemberFilterSame = false,
    isFetchingForMention = false
  ): Promise<any[]> {
    const groupMembers = await UsersService.getGroupMembersDetails(
      memberRole,
      memberStatus,
      searchText,
      paginationObject,
      showEmail
    )
    if (!isFetchingForMention) {
      if (this.state.groupMembers.length > 0 && isMemberFilterSame)
        this.state.groupMembers = [...this.state.groupMembers, ...groupMembers]
      else this.state.groupMembers = groupMembers
    }
    return groupMembers
  }
  async getGroupMembersDetailsForChannelSuggestions(
    channelId: string,
    searchText?: string,
    memberRole?: GroupMemberRole,
    paginationObject?: { limit: number; pageNo: number }
  ): Promise<any[]> {
    const groupMembers =
      await UsersService.getGroupMembersDetailsForChannelSuggestions(
        channelId,
        searchText,
        memberRole,
        paginationObject
      )
    return groupMembers
  }
  async fetchGroupMembers(): Promise<void> {
    await this.getGroupMembersDetails(
      undefined,
      GroupMemberStatus.ACTIVE,
      '',
      {
        limit: 10,
        pageNo: 1,
      },
      GroupState.state.isGroupAdmin ? true : false
    )
    this.state.groupMembersAvatarOptions = this.state.groupMembers.map(user => {
      return {
        ...user,
        src: user.avatar,
      }
    })
  }
  async getGroupMemberRequestCount(): Promise<void> {
    try {
      const requestCountResponse =
        await UsersService.getGroupMemberRequestCount()
      this.state.requestCount = requestCountResponse.count
    } catch (error) {
      console.error(error)
    }
  }
  async createGroup(groupData: IGroup): Promise<void> {
    const groups = new Groups(groupData)
    await groups.save()
  }
  async updateGroup(groupData: IGroup): Promise<void> {
    const updatedGroupData: IGroup = { ...groupData }

    if (isGokollab()) {
      updatedGroupData.slug = undefined
    }

    const group = new Groups({ ...updatedGroupData })
    await group.save()
    if (groupData.marketplaceGroupSlug) {
      await ListingsService.updateSlugByExternalId(
        GroupState.state.groupData._id.toString(),
        groupData.marketplaceGroupSlug
      )
    }
    this.setGroupDataInState(group.data.data, true)
  }
  async deleteGroup(): Promise<void> {}
  async getUserData(slug: string): Promise<any> {
    if (this.state.groupData._id) {
      return await UsersService.getUserDataBySlug(slug)
    }
    return null
  }
  async rearrangeChannels(
    updatedChannelSequence: [
      { id: string; channelName: string; sequence: number }
    ]
  ): Promise<void> {
    try {
      await GroupsService.updateChannelSequence(
        this.state.groupData._id,
        updatedChannelSequence
      )
    } catch (e) {
      console.error(e)
    }
  }
  updateShowChannelList(updatedValue: boolean): void {
    this.state.showChannelList = updatedValue
  }
  async getGroupMembersLevelDistribution(): Promise<Map<string, number>> {
    if (this.state.membersLevelDistribution.size > 0) {
      return this.state.membersLevelDistribution
    }
    const response = await GroupsService.getGroupMembersLevelDistribution(
      this.state.groupData._id
    )
    this.state.membersLevelDistribution = response
    return this.state.membersLevelDistribution
  }
  async submitMembershipAnswers(payload: {
    answers: (string | boolean[])[]
  }): Promise<any> {
    const response = await UsersService.submitMembershipAnswer({
      ...payload,
      formId: GroupState?.state?.groupData?.settings?.formId,
      contactId: UserState?.state?.currentUser?.contactId,
    })
    GroupState.state.userMembershipAnswers = {
      success: true,
      answers: payload?.answers,
      formId: GroupState?.state?.groupData?.settings?.formId,
      contactId: UserState?.state?.currentUser?.contactId,
    }
    return response
  }
  isActiveMember() {
    return this.state.role && this.state.userStatus === MemberStatus.ACTIVE
  }
  async fetchPinnedGroups(): Promise<void> {
    try {
      const isUserLoggedIn = !!UserState.state?.currentUser?.fullName

      if (!isUserLoggedIn) {
        this.state.pinnedGroupList = []
        return
      }

      if (!this.state.fetchingPinnedGroups) {
        this.state.fetchingPinnedGroups = true
        this.state.pinnedGroupList = []
        const response = await GroupsService.getPinnedGroups()
        this.state.pinnedGroupList = response
        this.state.fetchingPinnedGroups = false
      }
    } catch (error) {
      console.error(error)
    } finally {
      this.state.fetchingPinnedGroups = false
      await this.computeUnpinnedGroups()
    }
  }
  async computeUnpinnedGroups(): Promise<void> {
    this.state.pinnedGroupIds = this.state.pinnedGroupList.map(
      group => group.id
    )
    this.state.unpinnedGroupList = []

    for (let i = 0; i < this.state.groupList.length; i++) {
      const group = this.state.groupList[i]
      const formattedGroup: Group = {
        id: group._id,
        slug: group.slug,
        name: group.name,
        logo: group?.branding?.logo || DEFAULT_GROUP_LOGO,
        coverImage: group?.branding?.cover || DEFAULT_GROUP_COVER_IMAGE,
        membersCount: group?.membersCount || 0,
        type:
          group?.settings?.groupType?.toLowerCase() ===
          GroupType.PRIVATE.toLowerCase()
            ? GroupType.PRIVATE
            : GroupType.PUBLIC,
      }

      if (!this.state.pinnedGroupIds.includes(group._id)) {
        this.state.unpinnedGroupList.push(formattedGroup)
      }
    }
  }

  async pinGroup(groupId: string): Promise<void> {
    await GroupsService.pinGroup(groupId)
    await this.fetchPinnedGroups()
  }

  async unpinGroup(groupId: string): Promise<void> {
    await GroupsService.unpinGroup(groupId)
    await this.fetchPinnedGroups()
  }

  async syncPricing(groupId: string): Promise<void> {
    const response = await GroupsService.syncPricing(groupId)
    if (response?.settings?.membership) {
      this.state.groupData.settings.membership = response.settings.membership
      this.state.updatedGroupData.settings.membership =
        response.settings.membership
    }
  }
}

export const GroupState = new GroupsState()
